'use client';

import { useEffect, useState } from 'react';

/**
 * The gap between asking and knowing.
 *
 * Three things can go in that gap and only one of them is honest. An empty
 * state is a lie — "No clients are assigned to you yet" is an answer, and
 * nobody has answered yet. A spinner is honest but says nothing except
 * "wait", and it says it in the middle of a layout that then jumps when the
 * rows arrive. This is the third: the shape of what is coming, in the place it
 * will be, so the screen is already built when the content lands and the eye
 * has nothing to re-find.
 *
 * It shows nothing at all for the first fraction of a second. Most answers
 * arrive inside that, and a placeholder that flashes for 80ms is worse than no
 * placeholder — it reads as a glitch rather than as progress. Below the
 * threshold the screen simply stays as it was; above it, the shapes appear and
 * the reader understands the wait is real.
 */
const QUIET_MS = 220;

export function Waiting({
  rows = 4,
  variant = 'row',
  label = 'Loading',
  immediate = false,
}: {
  rows?: number;
  /** `row` for a list, `line` for a paragraph, `card` for a panel. */
  variant?: 'row' | 'line' | 'card';
  label?: string;
  /**
   * Skip the quiet pause. The pause assumes the previous screen is still
   * there; a tab that just removed it has nothing to stay as it was.
   */
  immediate?: boolean;
}) {
  const [show, setShow] = useState(immediate);

  useEffect(() => {
    if (immediate) return;
    const timer = setTimeout(() => setShow(true), QUIET_MS);
    return () => clearTimeout(timer);
  }, [immediate]);

  if (!show) return null;

  return (
    /* One live region for the whole block rather than per shape: a screen
       reader is told "loading" once, not once per grey rectangle. */
    <div className={`skel skel-${variant}`} role="status" aria-live="polite" aria-label={label}>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="skel-item" aria-hidden>
          <span className="skel-bar" />
          {variant === 'row' && <span className="skel-bar skel-bar-sub" />}
        </div>
      ))}
    </div>
  );
}
